import type { SerializedBlockNode } from '@payloadcms/richtext-lexical'
import { useTranslations } from 'next-intl'

import { BannerBlock } from '@/blocks/Banner/Component'
import { CallToActionBlock } from '@/blocks/CallToAction/Component'
import { CodeBlock, type CodeBlockProps } from '@/blocks/Code/Component'
import { DefinitionBlock } from '@/blocks/DefinitionBlock/Component'
import { EmbedBlock, type EmbedBlockProps } from '@/blocks/EmbedBlock/Component'
import { FaqBlock } from '@/blocks/FaqBlock/Component'
import { HowToBlock } from '@/blocks/HowToBlock/Component'
import { MediaBlock } from '@/blocks/MediaBlock/Component'
import RichTextForm from '@/components/RichText/RichTextForm'
import type {
  BannerBlock as BannerBlockProps,
  CallToActionBlock as CTABlockProps,
  ComparisonListingBlock as ComparisonListingBlockProps,
  DefinitionBlock as DefinitionBlockProps,
  FaqBlock as FaqBlockProps,
  HowToBlock as HowToBlockProps,
  MediaBlock as MediaBlockProps,
} from '@/payload-types'

import type { RichTextBlockConverterOptions } from './blockConvertersTypes'

export type { RichTextBlockConverterOptions } from './blockConvertersTypes'

function BlockClientPlaceholder({ label }: { label: string }) {
  const t = useTranslations('Blocks')
  return (
    <div className="col-start-2 my-3 rounded-md border border-dashed bg-muted/40 px-3 py-2 text-center text-sm text-muted-foreground">
      {t('previewPlaceholder', { label })}
    </div>
  )
}

/** Mesmas chaves que `blockConverters.server`, sem Payload (para `FormBlock` client). */
export function createRichTextBlockConvertersClient(options?: RichTextBlockConverterOptions) {
  let mediaBlockIndex = 0
  const postTitle = options?.postTitle
  const headings = options?.mediaNearestHeadings
  const embedStrings = options?.embedStrings
  const blockStrings = options?.blockStrings

  return {
    banner: ({ node }: { node: SerializedBlockNode<BannerBlockProps> }) => (
      <BannerBlock
        className="col-start-2 mb-4"
        {...node.fields}
        lexicalRichText={RichTextForm}
        ariaStrings={blockStrings}
      />
    ),
    embed: ({ node }: { node: SerializedBlockNode<EmbedBlockProps> }) => (
      <EmbedBlock
        className="col-start-2 my-4 w-full"
        {...node.fields}
        iframeTitle={embedStrings?.iframeTitle}
        ariaLabel={embedStrings?.fallbackAriaLabel}
      />
    ),
    comparisonListing: (_props: { node: SerializedBlockNode<ComparisonListingBlockProps> }) => (
      <BlockClientPlaceholder label="Listagem comparativa" />
    ),
    definition: ({ node }: { node: SerializedBlockNode<DefinitionBlockProps> }) => (
      <DefinitionBlock
        className="col-start-2"
        {...node.fields}
        labelText={blockStrings?.definitionLabel}
        ariaTemplate={blockStrings?.definitionAria}
      />
    ),
    faq: ({ node }: { node: SerializedBlockNode<FaqBlockProps> }) => (
      <FaqBlock className="col-start-2" {...node.fields} titleText={blockStrings?.faqTitle} />
    ),
    howTo: ({ node }: { node: SerializedBlockNode<HowToBlockProps> }) => (
      <HowToBlock className="col-start-2" {...node.fields} />
    ),
    mediaBlock: ({ node }: { node: SerializedBlockNode<MediaBlockProps> }) => {
      const idx = mediaBlockIndex++
      const nearest = headings?.[idx] ?? null
      return (
        <MediaBlock
          className="col-start-1 col-span-3 my-4"
          imgClassName="m-0"
          {...node.fields}
          staticAltFallback={blockStrings?.mediaStaticFallbackAlt}
          captionClassName="mx-auto max-w-[48rem] mt-2 text-sm"
          captionRichText={RichTextForm}
          disableInnerContainer={true}
          enableGutter={false}
          isFirstMediaInRichText={idx === 0}
          nearestHeading={nearest}
          postTitle={postTitle}
        />
      )
    },
    code: ({ node }: { node: SerializedBlockNode<CodeBlockProps> }) => (
      <CodeBlock className="col-start-2" {...node.fields} />
    ),
    cta: ({ node }: { node: SerializedBlockNode<CTABlockProps> }) => (
      <CallToActionBlock {...node.fields} lexicalRichText={RichTextForm} />
    ),
  }
}
